import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { BreadcrumbService } from 'angular-crumbs';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { Location, LocationStrategy, PathLocationStrategy } from '@angular/common';
import { AudioPlayerService } from './components/helper/audio/audio-player.service';
import { PlyrComponent } from 'ngx-plyr';
import { CookieConsentService } from './components/helper/cookie-consent.service';
import { TranslatesService } from './components/helper/translate/translate.service';
import { GoogleAnalyticsService } from './components/helper/googleAnalytics/google-analytics.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [
    Location, {
      provide: LocationStrategy,
      useClass: PathLocationStrategy
    }
  ]
})
export class AppComponent implements OnInit {
  @ViewChild('bottomPlayer', { static: false }) bottomPlayer!: PlyrComponent;
  showBottomAudio: boolean = false;
  audioUrl: string = '';
  progressPercentage: number = 0;
  duration!: string;
  audioTitle: string = '';
  audioId!: number | null;
  itemId!: number | null
  audioTime!: string;
  showCookieConsentBanner: boolean = false;

  constructor(private titleService: Title, 
    private breadcrumbService: BreadcrumbService,
    private router: Router,
    private translate: TranslateService,
    private _audioPlayerService: AudioPlayerService,
    private cdr: ChangeDetectorRef,
    private cookieConsentService: CookieConsentService,
    private _translateService: TranslatesService,
    private gaService: GoogleAnalyticsService
  ) {
    this.translate.setDefaultLang('en');
  }
  
  ngOnInit(): void {
    const consent = this.cookieConsentService.getConsent();

    if (consent === 'true') {
      this.initializeGA();
      this.showCookieConsentBanner = false;
    } else if (consent === '') {
      this.showCookieConsentBanner = true;
    } else {
      this.showCookieConsentBanner = false;
    }

    this._audioPlayerService.getAudioUrl().subscribe(url => {
      this.audioUrl = url;
    });

    this._audioPlayerService.getItemId().subscribe(itemId => {      
      this.itemId = itemId;
    })

    this._audioPlayerService.getAudioIdAsObservable().subscribe(id => {
      this.audioId = id;
    });


    this._audioPlayerService.getAudioTitle().subscribe(title => {
      this.audioTitle = title;
    });

    this._audioPlayerService.getShowBottomAudio().subscribe(show => {      
      this.showBottomAudio = show;
      this.cdr.detectChanges();
    });

    this._audioPlayerService.setPlayers(null, this.bottomPlayer);
    
    // this.breadcrumbService.breadcrumbChanged.subscribe(crumbs => {
    //   this.setPageTitle(crumbs);
    // });

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      const routeTitle = this.getRouteTitle(this.router.routerState.snapshot.root);
      if (routeTitle) {
        this.translate.get(routeTitle).subscribe((translatedTitle: string) => {
          this.titleService.setTitle(translatedTitle);
        });
      }
    });
  }

  onAudioPlay() {
    if (this._audioPlayerService.bottomPlayer) {
      this._audioPlayerService.updateIsPausedMap(this.audioId, !this._audioPlayerService.bottomPlayer?.player.paused);
    }
  }

  onAudioPause() { 
    if (this._audioPlayerService.bottomPlayer) {
      this._audioPlayerService.updateIsPausedMap(this.audioId, !this._audioPlayerService.bottomPlayer?.player.paused);
    }
  }

  onAudioClose() {
    if (this._audioPlayerService.bottomPlayer) {
      this._audioPlayerService.stopAudio();
    }
  }

  redirectToAudioDetails() {
    const lang = this._translateService.getCurrentLang() || 'en';
    this.router.navigate(['/item', this.itemId], {
      queryParams: { lang }
    });
  }

  onTimeUpdate(event: any) {
    const currentTime = this.bottomPlayer?.player.currentTime;
    const duration = this.bottomPlayer?.player.duration;
    this.audioTime = this.formatTime(event.detail.plyr.currentTime);
    
    if (currentTime && duration) {
      this.progressPercentage = (currentTime / duration) * 100;
      const remainingTime = duration - currentTime;
      this.duration = this._audioPlayerService.convertSecondsToTimeFormat(remainingTime);      
    }
  }

  onActivate(_event:any){
    window.scroll(0,0);
  }

  getRouteTitle(route: any): string | null {
    if (route.data && route.data['title']) {
      return route.data['title'];
    }
    if (route.firstChild) {
      return this.getRouteTitle(route.firstChild);
    }
    return null;
  }

  // setPageTitle(crumbs: any[]) {
  //   const defaultTitle = "Sacreva - Mega Church - Angular Template";
  //   const titles = crumbs.filter(crumb => crumb.displayName);

  //   if (!titles.length) { 
  //     this.titleService.setTitle(defaultTitle); 
  //     return; 
  //   }

  //   const pageTitle = this.titlesToString(titles);
  //   this.translate.get(pageTitle).subscribe((translatedTitle: string) => {
  //     this.titleService.setTitle(`${defaultTitle} | ${translatedTitle}`);
  //   });
  // }

  titlesToString(titles: any[]) {
    return titles.reduce((prev, curr) => {
      return `${prev} | ${curr.displayName}`;
    }, '');
  }

  formatTime(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    
    const minutesFormatted = minutes < 10 ? `0${minutes}` : `${minutes}`;
    const secondsFormatted = remainingSeconds < 10 ? `0${remainingSeconds}` : `${remainingSeconds}`;
    
    return `${minutesFormatted}:${secondsFormatted}`;
  }

  acceptCookies(): void {
    this.cookieConsentService.setConsent(true);
    this.showCookieConsentBanner = false;
    this.initializeGA();
  }

  declineCookies(): void {
    this.cookieConsentService.setConsent(false);
    this.showCookieConsentBanner = false;
  }

  private initializeGA(): void {
    this.gaService.init('G-EHJZTRDWY3');

    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        const gtag = (window as any).gtag;
        if (gtag) {
          gtag('config', 'G-EHJZTRDWY3', {
            page_path: event.urlAfterRedirects,
          });
        }
      }
    });
  }

}
